import React, { useEffect } from 'react';
import { Avatar, Card, Col, Descriptions, Row, Spin, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useSelfService } from '../../hooks/useSelfService';

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'green',
  INACTIVE: 'red',
  ON_LEAVE: 'gold',
  TERMINATED: 'default',
};

const SelfServiceProfileView: React.FC = () => {
  const { profile, loading, fetchProfile } = useSelfService();

  useEffect(() => {
    fetchProfile();
  }, []);

  if (loading) return <Spin className="p-6" />;

  if (!profile) {
    return (
      <div className="p-6">
        <Card>
          <p>No employee record is linked to your account. Please contact HR.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <Card className="mb-6">
        <div className="flex items-center gap-6">
          <Avatar size={96} icon={<UserOutlined />} style={{ background: '#1890ff', flexShrink: 0 }}>
            {`${profile.firstName?.[0]}${profile.lastName?.[0]}`}
          </Avatar>
          <div>
            <h2 className="text-2xl font-semibold mb-1">
              {profile.firstName} {profile.middleName} {profile.lastName}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <Tag>{profile.employeeNumber}</Tag>
              {profile.status && <Tag color={STATUS_COLOR[profile.status] ?? 'default'}>{profile.status}</Tag>}
              {profile.employmentType && <Tag color="blue">{profile.employmentType?.replace('_', ' ')}</Tag>}
            </div>
          </div>
        </div>
      </Card>

      <Row gutter={[16, 16]}>
        {/* Personal Info */}
        <Col xs={24} lg={12}>
          <Card title="Personal Information">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Email">{profile.email}</Descriptions.Item>
              <Descriptions.Item label="Phone">{profile.phoneNumber}</Descriptions.Item>
              <Descriptions.Item label="National ID">{profile.nationalId}</Descriptions.Item>
              <Descriptions.Item label="Address">{profile.address}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Employment Details */}
        <Col xs={24} lg={12}>
          <Card title="Employment Details">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Department">{profile.departmentName}</Descriptions.Item>
              <Descriptions.Item label="Position">{profile.positionTitle}</Descriptions.Item>
              <Descriptions.Item label="Hire Date">{profile.hireDate}</Descriptions.Item>
              <Descriptions.Item label="Employment Type">{profile.employmentType?.replace('_', ' ')}</Descriptions.Item>
              {profile.supervisorName && (
                <Descriptions.Item label="Supervisor">{profile.supervisorName}</Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        {/* Emergency Contact */}
        <Col xs={24} lg={12}>
          <Card title="Emergency Contact">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Name">{profile.emergencyContactName ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Phone">{profile.emergencyContactPhone ?? '—'}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SelfServiceProfileView;
